import { useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { APIEndpoint } from 'common/enum';
import {
  universalGetRequest,
  universalPostRequest,
} from 'common/requestHandler';
import { userTypes } from 'config/config';
import { popupNotification } from 'notifications/ToastNotification';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'redux/store';

import CustomDropdown from '../../../components/elements/CustomDropdown';
import HeaderComp from '../../../components/elements/HeadingComponent';
import InputElement from '../../../components/elements/InputElement';
import { validateInviteCorporateUser } from './validation';

interface CorporateInviteInterface {
  email: string;
  phone_number: string;
  firstName: string;
  lastName: string;
  corporateName: string;
  corporate?: string;
}

interface CorporateListInterface {
  id: string;
  name: string;
}

const InviteCorporate = () => {
  const [loading, setLoading] = useState(false);
  const { uuid } = useParams();
  const [inviteType, setInviteType] = useState<string>('new');
  const [corporateList, setCorporateList] = useState<CorporateListInterface[]>(
    []
  );
  const { companyId } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CorporateInviteInterface>({
    resolver: yupResolver(validateInviteCorporateUser),
    mode: 'onChange',
    context: { inviteType },
    defaultValues: {
      email: '',
      phone_number: '',
      firstName: '',
      lastName: '',
      corporateName: '',
      corporate: '',
    },
  });
  const corporateNameFromDropdown = watch('corporate');

  const submitHandler = async (data: CorporateInviteInterface) => {
    setLoading(true);

    const response = await universalPostRequest(
      APIEndpoint.inviteNewCorporate,
      {
        email: data.email,
        phone_number: data.phone_number,
        userType:
          inviteType === 'new'
            ? userTypes.corporateAdmin
            : userTypes.corporateStaff,
        firstName: data.firstName,
        lastName: data.lastName,
        companyId: uuid,
        corporateName: data.corporateName,
        action: inviteType === 'new' ? 'new' : '',
      }
    );
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      popupNotification(response.message, true);
      reset();
    }
    setLoading(false);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const getUsersList = useCallback(async () => {
    setLoading(true);
    const response: any = await universalGetRequest(APIEndpoint.getUsers, {
      companyId: companyId,
      actionName: 'GetCorporate',
      flowName: 'ForUsers',
    });

    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      setCorporateList(response.data);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      getUsersList();
    }
  }, [companyId, getUsersList]);

  useEffect(() => {
    if (
      inviteType === 'existing' &&
      corporateNameFromDropdown &&
      corporateNameFromDropdown.length
    ) {
      setValue('corporateName', corporateNameFromDropdown.split(' - ')[0]);
    }
  }, [corporateNameFromDropdown]);

  return (
    <Box>
      <HeaderComp>Invite Corporate</HeaderComp>

      {/* Back Button */}
      <Box
        display="flex"
        alignItems="center"
        mb={4}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateX(-4px)',
          },
        }}
        onClick={handleBackClick}
      >
        <IconButton
          size="small"
          sx={{
            backgroundColor: 'rgba(18, 112, 220, 0.1)',
            color: theme.palette.primary.main,
            mr: 1,
            '&:hover': {
              backgroundColor: 'rgba(18, 112, 220, 0.2)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="primary"
          sx={{
            fontWeight: 500,
            userSelect: 'none',
          }}
        >
          Back
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center" width="100%">
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: '600px',
            background: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            p: 4,
          }}
        >
          {/* Tab Navigation */}
          <Box display="flex" gap={2} mb={4}>
            <Button
              variant={inviteType === 'new' ? 'contained' : 'outlined'}
              startIcon={<BusinessIcon />}
              onClick={() => {
                setInviteType('new');
                reset({
                  email: '',
                  phone_number: '',
                  firstName: '',
                  lastName: '',
                  corporateName: '',
                  corporate: '',
                });
              }}
              sx={{
                background:
                  inviteType === 'new'
                    ? 'linear-gradient(45deg, #42d392, #647eff)'
                    : 'transparent',
                color: inviteType === 'new' ? '#ffffff' : '#000000',
                border: inviteType === 'new' ? 'none' : '1px solid #e0e0e0',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background:
                    inviteType === 'new'
                      ? 'linear-gradient(45deg, #3bbf7f, #5a6ee6)'
                      : '#f5f5f5',
                },
              }}
            >
              Invite new corporate
            </Button>
            <Button
              variant={inviteType === 'existing' ? 'contained' : 'outlined'}
              startIcon={<GroupIcon />}
              onClick={() => {
                setInviteType('existing');
                reset({
                  email: '',
                  phone_number: '',
                  firstName: '',
                  lastName: '',
                  corporateName: '',
                  corporate: '',
                });
              }}
              sx={{
                background:
                  inviteType === 'existing'
                    ? 'linear-gradient(45deg, #42d392, #647eff)'
                    : 'transparent',
                color: inviteType === 'existing' ? '#ffffff' : '#000000',
                border:
                  inviteType === 'existing' ? 'none' : '1px solid #e0e0e0',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background:
                    inviteType === 'existing'
                      ? 'linear-gradient(45deg, #3bbf7f, #5a6ee6)'
                      : '#f5f5f5',
                },
              }}
            >
              Invite corporate staff user
            </Button>
          </Box>

          {/* Form Title */}
          <Box mb={4}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#000000',
                mb: 1,
              }}
            >
              {inviteType === 'new'
                ? 'Corporate details'
                : 'Corporate user details'}
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(submitHandler)}>
            {inviteType === 'existing' && (
              <Box mb={3}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#000000',
                    mb: 1,
                  }}
                >
                  Select corporate
                </Typography>
                <CustomDropdown
                  name="corporate"
                  control={control}
                  options={corporateList}
                  label="Select corporate"
                  labelKey="name"
                  valueKey="name"
                  error={errors.corporate}
                />
              </Box>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    First name
                  </Typography>
                  <InputElement
                    name="firstName"
                    control={control}
                    placeholder="First name"
                    type="text"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    Last name
                  </Typography>
                  <InputElement
                    name="lastName"
                    control={control}
                    placeholder="Last name"
                    type="text"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    Corporate name
                  </Typography>
                  <InputElement
                    name="corporateName"
                    control={control}
                    placeholder="Corporate name"
                    type="text"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    Email
                  </Typography>
                  <InputElement
                    name="email"
                    control={control}
                    placeholder="Email"
                    type="email"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    Phone
                  </Typography>
                  <InputElement
                    name="phone_number"
                    control={control}
                    placeholder="Phone"
                    type="tel"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={4}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  background: 'linear-gradient(45deg, #42d392, #647eff)',
                  color: '#ffffff',
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #3bbf7f, #5a6ee6)',
                  },
                  '&:disabled': {
                    background: '#bdbdbd',
                    color: '#666666',
                  },
                }}
              >
                {loading
                  ? 'Inviting...'
                  : inviteType === 'new'
                    ? 'Invite corporate'
                    : 'Invite corporate user'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default InviteCorporate;
